import { useEffect, useState } from 'react';
import type { ParentBuddyRequestView } from '@kidzim/shared';
import { Avatar, Badge, Button, Card, ErrorState } from '../design-system/components';
import { api, type ApiError } from '../lib/api';

export function Buddies() {
  const [requests, setRequests] = useState<ParentBuddyRequestView[] | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  async function refresh() {
    setLoadError(null);
    try {
      const { requests } = await api<{ requests: ParentBuddyRequestView[] }>('/api/family/buddy-requests');
      setRequests(requests);
    } catch (err) {
      setRequests(null);
      setLoadError(err as ApiError);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function act(id: string, action: 'approve' | 'decline') {
    await api(`/api/family/buddy-requests/${id}/${action}`, { method: 'POST', body: '{}' });
    await refresh();
  }

  if (loadError) {
    const message =
      loadError.error === 'forbidden'
        ? "This page is for parents managing a family. As an admin, you don't have a family here."
        : loadError.error === 'no_family'
          ? "You don't currently belong to a family. Ask another grown-up in your household for an invite code, or contact support."
          : 'Could not load buddy requests.';
    return <ErrorState message={message} onRetry={loadError.error === 'forbidden' || loadError.error === 'no_family' ? undefined : refresh} />;
  }
  if (!requests) return <p className="kz-muted">Loading…</p>;

  const actionable = requests.filter((r) => r.actionable);
  const waiting = requests.filter((r) => !r.actionable);

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)', maxWidth: 640 }}>
      <div>
        <span className="kz-eyebrow">Buddy requests</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Connections for your kids</h1>
        <p className="kz-muted">
          Kids can ask to add a buddy by their family code and screen name. Nothing connects until you say yes. You can
          change whether the other family also has to approve in <strong>Safety settings</strong>.
        </p>
      </div>

      <section>
        <h3 style={{ marginBottom: 'var(--space-3)' }}>Needs your OK</h3>
        {actionable.length === 0 ? (
          <Card>
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>Nothing waiting on you right now. 🎉</p>
          </Card>
        ) : (
          <div className="kz-stack" style={{ gap: 'var(--space-3)' }}>
            {actionable.map((r) => (
              <Card key={r.id} className="kz-row" style={{ justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <div className="kz-row" style={{ gap: 'var(--space-3)' }}>
                  <Avatar name={r.ourKid.screenName} />
                  <div>
                    <p style={{ font: 'var(--type-body-strong)' }}>
                      {r.side === 'requester' ? (
                        <>{r.ourKid.screenName} wants to add {r.otherScreenName}</>
                      ) : (
                        <>{r.otherScreenName} wants to be buddies with {r.ourKid.screenName}</>
                      )}
                    </p>
                    <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                      {r.side === 'requester' ? 'Your child asked' : 'Request from another family'}
                    </p>
                  </div>
                </div>
                <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                  <Button size="sm" onClick={() => act(r.id, 'approve')}>Approve</Button>
                  <Button variant="ghost" size="sm" onClick={() => act(r.id, 'decline')}>Decline</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {waiting.length > 0 && (
        <section>
          <h3 style={{ marginBottom: 'var(--space-3)' }}>Waiting on the other family</h3>
          <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
            {waiting.map((r) => (
              <div key={r.id} className="kz-row" style={{ justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ font: 'var(--type-body-strong)' }}>
                  {r.ourKid.screenName} &amp; {r.otherScreenName}
                </span>
                <Badge tone="neutral">Pending</Badge>
              </div>
            ))}
          </Card>
        </section>
      )}
    </div>
  );
}
